import Checkbox from 'app/components/checkbox';
import Dropdown from 'app/components/Dropdown';
import HeaderWithBackButton from 'app/components/HeaderWithBackButton';
import MultiSelectDropdown from 'app/components/MultiSelectDropdown';
import NewAttachment from 'app/components/NewAttachment';
import NewInput from 'app/components/NewInput';
import PageLoader from 'app/components/PageLoader';
import NumberInput from 'app/components/UI/NumberInput';
import Button from 'app/components/button';
import Toggle from 'react-toggle';
import {
  GAME_TYPE_LABELS,
  TRIGGER_TYPE_LABELS,
} from 'app/constants/campaign-constants';
import { commonMaxDate, commonMinDate } from 'app/utils/Parsers';
import useCreateCampaign from './useCreateCampaign';
import {
  getUncoveredTargets,
  hasPointsConfigured,
  ACCRUAL_OPTION,
  buildWinsPerDayHint,
} from './submitHelpers';
import './index.scss';

// Target Gender is intentionally NOT the shared title-case gender-options
// lookup (app/utils' shared dropdown-options module) — backend
// TARGET_GENDERS is upper-case (ALL/MALE/FEMALE/OTHER). Locally scoped to
// avoid a guaranteed Phase 3 rework and to avoid mutating the shared array
// (Open Question 1, resolved).
const TARGET_GENDER_OPTIONS = [
  { label: 'All', value: 'ALL' },
  { label: 'Male', value: 'MALE' },
  { label: 'Female', value: 'FEMALE' },
  { label: 'Other', value: 'OTHER' },
];

// Shown on hover when the bulk voucher actions are disabled. Names a mall AND
// a tier because targeting is per (mall, tier) pair — ticking a mall without
// choosing a tier still leaves nothing for these buttons to act on.
const NO_TARGETS_HINT = 'Select at least one mall and tier first';

const GAME_TYPE_OPTIONS = Object.keys(GAME_TYPE_LABELS).map((key) => ({
  label: GAME_TYPE_LABELS[key],
  value: key,
}));

const TRIGGER_TYPE_OPTIONS = Object.keys(TRIGGER_TYPE_LABELS).map((key) => ({
  id: `trigger-${key.toLowerCase().replace(/_/g, '-')}`,
  label: TRIGGER_TYPE_LABELS[key],
  value: key,
}));

const CreateCampaign = () => {
  const {
    values,
    error,
    fieldErrors,
    tierList,
    pageLoading,
    slabUpgradePaths,
    targetingEntries,
    groupedMalls,
    bulkMinSpend,
    setBulkMinSpend,
    bulkWinsPerDay,
    setBulkWinsPerDay,
    toggleMallOpen,
    toggleMallTier,
    toggleMall,
    toggleAllMalls,
    applyBulkDefaults,
    bulkVouchers,
    setEntryMinSpend,
    setEntryWinsPerDay,
    setEntryVouchers,
    togglePublish,
    toggleAlwaysWin,
    isPastEndDate,
    publishDisplayValue,
    publishBlockedByEndDate,
    issuanceRuleUnmet,
    issuanceOffendingRows,
    // CAMP-07 edit-mode: the raw campaign detail response. Read here ONLY for
    // the remaining-points hint under Total Points Budget — the form's own
    // editable state lives in `values`, never in `detail`.
    detail,
    campaignId,
    isLocked,
    isVoucherRemovable,
    handleRemoveAssignedVoucher,
    // Voucher-quantity editing is temporarily hidden (kept in code for an easy
    // re-enable) — see the commented-out Quantity editor in the
    // assigned-voucher card below. The hook still exposes these.
    // voucherUpdates,
    // voucherUpdateErrors,
    // handleVoucherUpdateChange,
    handleChange,
    addUpgradePath,
    removeUpgradePath,
    handleUpgradePathChange,
    uploadMediaHandler,
    handleCreateCampaign,
    navigate,
  } = useCreateCampaign();

  // The bulk voucher actions operate on the targeted (mall, tier) pairs, so
  // with none selected they do nothing at all. Gate on the PAIR count, not on
  // "a mall is ticked": a mall with no tier chosen still yields no pairs.
  const noTargetsSelected = targetingEntries.length === 0;

  // Does this campaign read bills? Mirrors the server's
  // assertBillDatesRequiredWhenBillReading (09-06) rather than re-deriving the
  // condition, so the two cannot drift: the SINGLE_DAY_SPEND trigger, OR any
  // targeted row carrying a minSpendThreshold. Note the trigger is irrelevant
  // beyond SINGLE_DAY_SPEND — a BIRTHDAY campaign with a per-pool min spend
  // reads bills just the same, which is the case that surprised the developer.
  const hasValue = (v) => v !== '' && v !== null && v !== undefined;
  const readsBills =
    values.triggerType === 'SINGLE_DAY_SPEND' ||
    targetingEntries.some((entry) => hasValue(entry.minSpendThreshold));
  const billDatesMissing =
    readsBills && (!hasValue(values.billStartDate) || !hasValue(values.billEndDate));

  // RWD-04/D-05: inline coverage summary — display-only, computed from the
  // canonical flat targetingEntries array. The submit-time D-05 confirm
  // (checkCoverageAndSubmit/useCreateCampaign.js) is unchanged; this just
  // surfaces the same signal inline at the bottom of the section instead of
  // only at Save.
  const uncoveredCount = getUncoveredTargets(targetingEntries).length;
  // Quick task 260819-e3y-rewrite-admin-campaign-save-validation: a
  // voucher-less target is only a problem when the campaign has no points
  // budget to fall back on — once points are set, it's informational.
  const pointsConfigured = hasPointsConfigured(values);
  const coverageLine =
    targetingEntries.length === 0
      ? 'No targets selected.'
      : uncoveredCount > 0
        ? pointsConfigured
          ? `${uncoveredCount} of ${targetingEntries.length} targets have no voucher — they'll reward points instead.`
          : `${uncoveredCount} of ${targetingEntries.length} targets have no voucher — add one to each, or set a points budget.`
        : `All ${targetingEntries.length} targets have vouchers assigned.`;

  // Wins-per-day guidance, summarised rather than one line per offending
  // (mall, tier) pair — see buildWinsPerDayHint for why, and for the sizing
  // rules. Lives in submitHelpers so it is unit-testable without standing up
  // the whole form.
  const issuanceHint = buildWinsPerDayHint(
    issuanceOffendingRows,
    targetingEntries
  );

  // Top "All malls" tri-state derives from groupedMalls (itself derived from
  // targetingEntries in the hook) — every (mall,tier) pair selected across
  // every mall = checked, none = unchecked, anything else = partial.
  const totalTierPairs = groupedMalls.reduce(
    (sum, mall) => sum + mall.tiers.length,
    0
  );
  const mallsWithTargets = groupedMalls.filter(
    (mall) => mall.status !== 'unchecked'
  ).length;
  const allMallsStatus =
    totalTierPairs > 0 && targetingEntries.length === totalTierPairs
      ? 'checked'
      : targetingEntries.length > 0
        ? 'partial'
        : 'unchecked';

  return (
    <div className='blogsForm campaign-form'>
      <div className='blogsForm_content'>
        <HeaderWithBackButton
          title={campaignId ? 'Edit Campaign' : 'Create Campaign'}
        />

        {/* Basics (D-05) — UAT rework (post-09-11/09-12 developer review):
            Campaign Name / Bill Start / Bill End, then Game Type / Start Date
            / End Date, then Target Gender on its own row. Always Win AND
            Publish both moved out of this block entirely — they sit together
            as toggle cards just above the Save button (see below). Section-
            heading prose and helper paragraphs flagged in UAT were removed;
            field labels are unchanged. */}
        <div className='inner-form-wrapper flex justify-between flex-wrap mb-4'>
          <strong className='wd-100 mb-2 campaign-section-title'>Basics</strong>
          {/* Row 1: Campaign Name · Bill Start Date · Bill End Date. The bill
              pair renders unconditionally (UAT round 3): it was previously
              gated on a client-side `readsBills` guess, which hid both fields
              on a fresh form — the trigger is not yet SINGLE_DAY_SPEND and no
              targeting row has a minSpendThreshold at that point, so the
              admin could never reach the inputs. The server
              (assertBillDatesRequiredWhenBillReading) decides when these are
              REQUIRED; the form just offers them (D-11's "the dropdown is UX,
              not the security boundary" — 09-06-SUMMARY.md). */}
          <div className='wd-33'>
            <NewInput
              label='Campaign Name*'
              id='name'
              name='name'
              type='text'
              placeholder='Enter a campaign name'
              value={values?.name}
              onChange={handleChange}
              errorText={error?.name}
            />
          </div>
          {/* The bill window is RETROSPECTIVE by nature — it asks "which
              invoice dates count", which is routinely a period that has
              already passed. Example: a Christmas promotion advertised as
              "spend on the 25th", set up on the 27th once the bills are in.
              So NO `min` bound here: past dates must be selectable. (The
              campaign window below is different — it is game availability,
              so it still floors at today.) The server has no future-date
              rule on these either; it only requires them to be PRESENT when
              the campaign reads bills (09-06). */}
          <div className='wd-33'>
            <NewInput
              label='Bill Start Date'
              id='billStartDate'
              name='billStartDate'
              type='date'
              max={commonMaxDate()}
              value={values?.billStartDate}
              onChange={handleChange}
              errorText={error?.billStartDate}
              disabled={isLocked}
            />
          </div>
          <div className='wd-33'>
            <NewInput
              label='Bill End Date'
              id='billEndDate'
              name='billEndDate'
              type='date'
              min={values.billStartDate || undefined}
              max={commonMaxDate()}
              value={values?.billEndDate}
              onChange={handleChange}
              errorText={error?.billEndDate}
            />
          </div>

          {/* Row 2: Game Type · Target Gender. */}
          <div className='wd-33'>
            <Dropdown
              id='gameType'
              label='Game Type*'
              name='gameType'
              options={GAME_TYPE_OPTIONS}
              placeholder='Select game type'
              value={values.gameType}
              handleChange={handleChange}
              errorText={error?.gameType}
            />
          </div>
          <div className='wd-33'>
            <Dropdown
              id='targetGender'
              label='Target Gender'
              name='targetGender'
              options={TARGET_GENDER_OPTIONS}
              placeholder='Select gender'
              value={values.targetGender}
              handleChange={handleChange}
              errorText={error?.targetGender}
              isDisabled={isLocked}
            />
          </div>
          <div className='wd-33' aria-hidden='true' />

          {/* The campaign window (startDate/endDate) used to sit here. It is
              HIDDEN, not removed (developer, UAT round 7) — the same two
              inputs now render under Trigger & Rules, but only for the
              DATE_BASED trigger, where the window is the trigger's ONLY
              condition (GameEligibilityService's DATE_BASED case returns
              eligible: true and leans entirely on the Step 1 window gate).
              Every other trigger carries its own rule — bill dates, birthday
              window, slab event — so the campaign window is just optional
              availability there and does not earn a slot in Basics.

              The state wiring is untouched: values.startDate/endDate, the
              submit mapping and the buildCampaignFormData appends all still
              exist, so a campaign edited on a non-DATE_BASED trigger keeps
              whatever window it already had. Restore by moving these two
              blocks back here (and dropping the DATE_BASED wrapper below).

          <div className='wd-33'>
            <NewInput
              label='Campaign Start Date'
              id='startDate'
              name='startDate'
              type='date'
              min={commonMinDate()}
              max={commonMaxDate()}
              value={values?.startDate}
              onChange={handleChange}
              errorText={error?.startDate}
              disabled={isLocked}
            />
          </div>
          <div className='wd-33'>
            <NewInput
              label='Campaign End Date'
              id='endDate'
              name='endDate'
              type='date'
              min={values.startDate ? values.startDate : commonMinDate()}
              max={commonMaxDate()}
              value={values?.endDate}
              onChange={handleChange}
              errorText={error?.endDate}
            />
          </div>
          */}
        </div>

        {/* Bonus Points (optional) — consolation reward issued when a window's
            vouchers are exhausted. Leave both Bonus Points fields blank to
            run a vouchers-only campaign. */}
        <div className='inner-form-wrapper flex justify-between flex-wrap mb-4'>
          <strong className='wd-100 mb-2 campaign-section-title'>
            Bonus Points
          </strong>
          <div className='wd-33'>
            <NumberInput
              label='Total Points Budget'
              id='totalPointsBudget'
              name='totalPointsBudget'
              min='0'
              placeholder='Enter total points budget'
              value={values?.totalPointsBudget}
              onChange={handleChange}
              errorText={error?.totalPointsBudget}
              disabled={isLocked}
            />
            {/* Edit-mode parity with the per-voucher "N remaining" line on the
                assigned-voucher cards below: an admin could see how much
                voucher stock was left but not how much of the points budget
                was. `remainingPoints` is taken verbatim from the campaign
                detail response (budget minus points already won, live
                unsettled holds counted as spent) and is NEVER recomputed here
                from `values` — the server's figure is the only one that
                matches what the pacing engine actually paces against.

                `!= null` is the only correct guard. The API returns null (not
                0) when the campaign has no points configured, precisely so
                that "never set up" and "budget exhausted" stay
                distinguishable; a truthiness test would silently hide a
                genuine 0-remaining campaign, which is the one an admin most
                needs to see.

                Snapshot from page load: it deliberately does not track edits
                to the field above until the campaign is saved and reloaded —
                same contract as the voucher remaining counts. */}
            {detail?.remainingPoints != null && (
              <p className='campaign-hint-text'>
                {`${detail.remainingPoints} points remaining`}
              </p>
            )}
          </div>
          <div className='wd-33'>
            <NumberInput
              label='Points Per Win'
              id='pointsPerWin'
              name='pointsPerWin'
              min='1'
              placeholder='Enter points per win'
              value={values?.pointsPerWin}
              onChange={handleChange}
              errorText={error?.pointsPerWin}
              disabled={isLocked}
            />
          </div>
          {/* Keep the two Bonus Points fields left-aligned rather than
              justify-between pushing Points Per Win to the far right. */}
          <div className='wd-33' aria-hidden='true' />
        </div>

        {/* Trigger & Rules (D-05) */}
        <div className='inner-form-wrapper flex justify-between flex-wrap mb-4'>
          <strong className='wd-100 mb-2 campaign-section-title'>Trigger & Rules</strong>
          <div className='wd-33 mb-2'>
            <Dropdown
              id='triggerType'
              label='Select Trigger Type*'
              name='triggerType'
              options={TRIGGER_TYPE_OPTIONS}
              placeholder='Select trigger type'
              value={values.triggerType}
              handleChange={handleChange}
              errorText={error?.triggerType}
              isDisabled={isLocked}
            />
          </div>
          <div className='wd-33' aria-hidden='true' />
          <div className='wd-33' aria-hidden='true' />

          {values.triggerType === 'SLAB_UPGRADE' && (
            <div className='wd-100'>
              <Button
                className='cancel-button btn-sm mb-2'
                type='button'
                onClick={addUpgradePath}
                variant='Add'
                name='+ Add Rule'
                isDisabled={isLocked}
              />
              {slabUpgradePaths.length === 0 ? (
                <div className='upgrade-path-empty-state mb-2'>
                  <strong className='campaign-empty-state-title'>
                    No upgrade paths added yet
                  </strong>
                  <p className='campaign-hint-text'>
                    Add at least one From-tier &rarr; To-tier path to define
                    this trigger.
                  </p>
                </div>
              ) : (
                slabUpgradePaths.map((row) => {
                  // Cross-filter: a tier already selected on the opposite
                  // side of this row is excluded so the same tier cannot be
                  // picked as both From and To (same-tier upgrades are
                  // logically meaningless). An unset opposite side yields
                  // the full tierList.
                  // Accrual (UI proxy -1 -> backend 0) is a valid FROM tier,
                  // listed first. Never added to toTierOptions — upgrading TO
                  // accrual isn't supported (quick task 260730-m22).
                  const fromTierOptions = [
                    ACCRUAL_OPTION,
                    ...(row.toTierId == null
                      ? tierList
                      : tierList.filter(
                          (opt) => String(opt.value) !== String(row.toTierId)
                        )),
                  ];
                  const toTierOptions =
                    row.fromTierId == null
                      ? tierList
                      : tierList.filter(
                          (opt) =>
                            String(opt.value) !== String(row.fromTierId)
                        );

                  return (
                    <div
                      key={row.addid}
                      className='flex gap-10 mb-2 campaign-row-align-end'
                    >
                      <div className='wd-33'>
                        <Dropdown
                          id={`fromTierId-${row.addid}`}
                          label='From Tier*'
                          name='fromTierId'
                          options={fromTierOptions}
                          placeholder='Select from tier'
                          value={row.fromTierId}
                          handleChange={(e) =>
                            handleUpgradePathChange(
                              row.addid,
                              'fromTierId',
                              e.target.value
                            )
                          }
                          isDisabled={isLocked}
                        />
                      </div>
                      <div className='wd-33'>
                        <Dropdown
                          id={`toTierId-${row.addid}`}
                          label='To Tier*'
                          name='toTierId'
                          options={toTierOptions}
                          placeholder='Select to tier'
                          value={row.toTierId}
                          handleChange={(e) =>
                            handleUpgradePathChange(
                              row.addid,
                              'toTierId',
                              e.target.value
                            )
                          }
                          isDisabled={isLocked}
                        />
                      </div>
                      {!isLocked && (
                        <Button
                          type='button'
                          className='cancel-button btn-sm'
                          onClick={() => removeUpgradePath(row.addid)}
                          variant='Remove'
                          name='Remove'
                        />
                      )}
                    </div>
                  );
                })
              )}
              {error?.slabUpgradePaths && (
                <span className='error-text'>{error.slabUpgradePaths}</span>
              )}
            </div>
          )}

          {values.triggerType === 'SINGLE_DAY_SPEND' && (
            <>
              <div className='wd-50'>
                <NumberInput
                  label='Minimum Amount*'
                  id='singleDaySpendMin'
                  name='singleDaySpendMin'
                  placeholder='Enter minimum amount'
                  value={values?.singleDaySpendMin}
                  onChange={handleChange}
                  errorText={error?.singleDaySpendMin}
                  disabled={isLocked}
                />
              </div>
              <div className='wd-50'>
                <NumberInput
                  label='Maximum Amount'
                  id='singleDaySpendMax'
                  name='singleDaySpendMax'
                  placeholder='Enter maximum amount (optional)'
                  value={values?.singleDaySpendMax}
                  onChange={handleChange}
                  errorText={error?.singleDaySpendMax}
                  disabled={isLocked}
                />
              </div>
            </>
          )}

          {values.triggerType === 'BIRTHDAY' && (
            <div className='wd-50'>
              <NumberInput
                label='Days Before Birthday*'
                id='birthdayWindowDays'
                name='birthdayWindowDays'
                placeholder='Enter days window'
                value={values?.birthdayWindowDays}
                onChange={handleChange}
                errorText={error?.birthdayWindowDays}
              />
            </div>
          )}

          {/* DATE_BASED's own parameters ARE the campaign window — the trigger
              has no condition of its own, so this window is the whole rule.
              Rendered here beside the other per-trigger parameters (rather
              than in Basics) for the same reason Min/Max Amount live here.
              Same fields, same state — only the location is conditional, so
              switching triggers never drops a window already entered. */}
          {values.triggerType === 'DATE_BASED' && (
            <>
              <div className='wd-50'>
                <NewInput
                  label='Campaign Start Date*'
                  id='startDate'
                  name='startDate'
                  type='date'
                  min={commonMinDate()}
                  max={commonMaxDate()}
                  value={values?.startDate}
                  onChange={handleChange}
                  errorText={error?.startDate}
                  disabled={isLocked}
                />
              </div>
              <div className='wd-50'>
                <NewInput
                  label='Campaign End Date*'
                  id='endDate'
                  name='endDate'
                  type='date'
                  min={values.startDate ? values.startDate : commonMinDate()}
                  max={commonMaxDate()}
                  value={values?.endDate}
                  onChange={handleChange}
                  errorText={error?.endDate}
                />
              </div>
            </>
          )}
        </div>

        {/* Assets (D-05) */}
        <div className='inner-form-wrapper flex justify-between flex-wrap mb-4'>
          <strong className='wd-100 mb-2 campaign-section-title'>Assets</strong>
          {/* Banner Image is intentionally not rendered — the product no
              longer uses a campaign banner. The bannerImage state/submit
              plumbing is deliberately left in place (useCreateCampaign's
              hydration + buildCampaignFormData/buildPostStartFormData
              appends) so editing a campaign that already has a stored banner
              re-sends its existing value instead of silently clearing it. */}
          <div className='wd-50'>
            <label>Background Image</label>
            <NewAttachment
              value={values?.backgroundImage}
              id='backgroundImage'
              onFileSelected={uploadMediaHandler}
              label='Choose File'
              alt='backgroundImage'
              sizevalidationUpto={2}
              errorText={error?.backgroundImage}
              dimesnsionLabel='(1080*1920)'
            />
          </div>
        </div>

        {/* CAMP-07/D-09: structural-lock note, shown once directly above the
            Targeting section once the campaign has started (status !==
            SCHEDULED) — exact wording locked by CONTEXT.md, not discretionary. */}
        {isLocked && (
          <p className='info-text mb-2'>
            Campaign has started — trigger type, tiers, spend thresholds, and
            win probability are locked. You can still change the end date, days
            before birthday, add vouchers, and adjust voucher quantities.
          </p>
        )}

        {/* Targeting (RWD-01/02/03/04, D-01..D-05, CAMP-07/D-09..D-12) —
            quick task 260720-ntx: mall-grouped accordion table. One row per
            mall with toggleable tier pills; selecting a tier auto-expands
            the mall's per-tier Min Spend + voucher picker detail panel. */}
        <div className='inner-form-wrapper targeting-accordion mb-4'>
          <strong className='campaign-section-title mb-2'>Targeting</strong>

          {values.triggerType === 'SLAB_UPGRADE' && (
            <p className='campaign-hint-text targeting-slab-hint'>
              {slabUpgradePaths.some((r) => r.fromTierId && r.toTierId)
                ? 'Slab Upgrade: you can only target the tiers your rules upgrade members into. Each upgrade destination must be targeted in at least one mall.'
                : 'Add a complete upgrade rule (From-Tier → To-Tier) above to choose which tiers you can target.'}
            </p>
          )}

          <div className='targeting-header-strip'>
            <button
              type='button'
              className='targeting-tri-state'
              onClick={toggleAllMalls}
              disabled={isLocked}
            >
              <span className={`targeting-tri-state-box is-${allMallsStatus}`}>
                {allMallsStatus === 'checked'
                  ? '✓'
                  : allMallsStatus === 'partial'
                    ? '–'
                    : ''}
              </span>
              <span>All malls</span>
            </button>
            <span className='targeting-summary-count'>
              <strong>{mallsWithTargets}</strong> of{' '}
              <strong>{groupedMalls.length}</strong> malls ·{' '}
              <strong>{targetingEntries.length}</strong> targets
            </span>
          </div>

          <div className='targeting-bulk-toolbar'>
            <div className='targeting-bulk-minspend'>
              <NumberInput
                label={undefined}
                id='bulkMinSpend'
                name='bulkMinSpend'
                placeholder='Minimum spend, e.g. 2000'
                value={bulkMinSpend}
                onChange={(e) => setBulkMinSpend(e.target.value)}
                disabled={isLocked}
              />
            </div>
            <div className='targeting-bulk-winsperday'>
              <NumberInput
                label={undefined}
                id='bulkWinsPerDay'
                name='bulkWinsPerDay'
                placeholder='Wins per day, e.g. 50'
                value={bulkWinsPerDay}
                onChange={(e) => setBulkWinsPerDay(e.target.value)}
                disabled={isLocked}
              />
            </div>
            <Button
              type='button'
              className='btn-sm'
              onClick={() => applyBulkDefaults('all')}
              name='Apply to all'
              isDisabled={isLocked}
            />
            <Button
              type='button'
              className='cancel-button btn-sm'
              onClick={() => applyBulkDefaults('empty')}
              name='Fill empty only'
              isDisabled={isLocked}
            />
            {/* Both voucher actions map over targetingEntries, so with no
                targeted (mall, tier) pair they are silent no-ops — the admin
                clicks and nothing happens. Disable them and say why.

                The title sits on a WRAPPER, not on the Button: a disabled
                button suppresses pointer events, so a title on the button
                itself never surfaces a tooltip. The span still receives hover
                and shows it. Native title matches the convention already used
                by the per-row override dot below. */}
            <span
              className='targeting-bulk-action'
              title={noTargetsSelected ? NO_TARGETS_HINT : undefined}
            >
              <Button
                type='button'
                className='cancel-button btn-sm'
                onClick={() => bulkVouchers('all')}
                name='All vouchers'
                isDisabled={isLocked || noTargetsSelected}
              />
            </span>
            <span
              className='targeting-bulk-action'
              title={noTargetsSelected ? NO_TARGETS_HINT : undefined}
            >
              <Button
                type='button'
                className='cancel-button btn-sm'
                onClick={() => bulkVouchers('clear')}
                name='Clear vouchers'
                isDisabled={isLocked || noTargetsSelected}
              />
            </span>
          </div>

          {groupedMalls.length === 0 ? (
            <div className='upgrade-path-empty-state mb-2'>
              <strong className='campaign-empty-state-title'>
                No malls available
              </strong>
              <p className='campaign-hint-text'>
                No malls are assigned to your account yet.
              </p>
            </div>
          ) : (
            <div className='targeting-mall-list'>
              {groupedMalls.map((mall) => (
                <div key={mall.mallId} className='targeting-mall-row'>
                  <div className='targeting-mall-summary'>
                    {/* The tri-state box and the mall name were one button,
                        both selecting every tier. Clicking the name on a mall
                        that ALREADY has targets then re-toggled the whole
                        selection when the admin only wanted to open or close
                        the rows — so the setup was reachable only via the
                        Set up / Hide setup button.

                        They are now two buttons with different jobs: the box
                        always selects/clears every tier; the name is a pure
                        disclosure control once the mall has targets, and only
                        falls back to select-all on an untouched mall.

                        Wrapped in one container because .targeting-mall-summary
                        is a THREE-column grid — a fourth child would wrap onto
                        its own row rather than sit inline. */}
                    <div className='targeting-mall-toggle-group'>
                      <button
                        type='button'
                        className='targeting-tri-state targeting-mall-toggle'
                        onClick={() => toggleMall(mall.mallId)}
                        disabled={isLocked}
                        title={
                          mall.status === 'unchecked'
                            ? 'Select all tiers'
                            : 'Clear all tiers'
                        }
                      >
                        <span
                          className={`targeting-tri-state-box is-${mall.status}`}
                        >
                          {mall.status === 'checked'
                            ? '✓'
                            : mall.status === 'partial'
                              ? '–'
                              : ''}
                        </span>
                      </button>
                      <button
                        type='button'
                        className='targeting-mall-name-button'
                        onClick={() =>
                          // Once a mall has targets, its name is purely a
                          // disclosure control — it mirrors whatever the
                          // Set up / Hide setup button would do, in both
                          // directions. Expanding to look and then clicking
                          // again to close must not wipe the selection.
                          // Select-all survives only for an untouched mall,
                          // where there is nothing to disclose and the click
                          // can only mean "target this mall".
                          mall.rows.length > 0
                            ? toggleMallOpen(mall.mallId)
                            : toggleMall(mall.mallId)
                        }
                        disabled={isLocked}
                      >
                        <span className='targeting-mall-name-block'>
                          <span className='targeting-mall-name'>
                            {mall.mallName}
                          </span>
                          <span className='targeting-mall-meta'>
                            {mall.city ? `${mall.city} · ` : ''}
                            {mall.summary}
                          </span>
                        </span>
                      </button>
                    </div>

                    <div className='targeting-tier-pills'>
                      {mall.tiers.map((tier) => (
                        <button
                          key={tier.tierId}
                          type='button'
                          className={`targeting-tier-pill ${
                            tier.isSelected ? 'is-active' : ''
                          } ${tier.slabDisabled ? 'is-disabled' : ''}`}
                          onClick={() =>
                            toggleMallTier(mall.mallId, tier.tierId)
                          }
                          disabled={isLocked || tier.slabDisabled}
                          title={tier.slabDisabledReason || undefined}
                        >
                          {tier.name}
                        </button>
                      ))}
                    </div>

                    <button
                      type='button'
                      className='targeting-disclose'
                      onClick={() => toggleMallOpen(mall.mallId)}
                      disabled={mall.rows.length === 0}
                    >
                      <span
                        className={`targeting-caret ${
                          mall.isOpen ? 'is-open' : ''
                        }`}
                      >
                        &rsaquo;
                      </span>
                      {mall.isOpen ? 'Hide setup' : 'Set up'}
                    </button>
                  </div>

                  {mall.isOpen && mall.rows.length > 0 && (
                    <div className='targeting-mall-panel'>
                      <div className='targeting-panel-header'>
                        <span>Tier</span>
                        <span>Minimum Spend</span>
                        <span>Wins per day</span>
                        <span>Vouchers — this mall&rsquo;s pool</span>
                      </div>
                      {mall.rows.map((row) => {
                        const entryIdx = targetingEntries.findIndex(
                          (e) => e.addid === row.addid
                        );
                        const assignedIds = new Set(
                          (row.assignedVouchers || []).map((v) => v.id)
                        );
                        // Quick task 260820-gyo (D-1/H5): the pool now lists
                        // any voucher with STOCK remaining, not only ones
                        // this campaign already owns — a voucher another
                        // campaign already holds is offered too as long as
                        // stock is left. Still drop the ones currently in
                        // the assigned-checkbox list — they'd otherwise show
                        // as both an addable dropdown option and a
                        // checkbox. Once unchecked, a voucher leaves
                        // assignedVouchers and reappears here, becoming
                        // selectable again.
                        //
                        // The option label stays the plain voucher title. A
                        // remaining count was tried here and read badly next
                        // to titles that already carry a rupee value
                        // ("... worth Rs. 1000 · 492 left"). Stock is already
                        // surfaced twice nearby: the "N vouchers remaining"
                        // hint beside Wins per day, and each attached
                        // voucher's own card. Sharing shows on that card too.
                        const voucherOptions = row.voucherPool
                          .filter((v) => !assignedIds.has(v.id))
                          .map((v) => ({
                            name: v.voucherTitle || `Voucher #${v.id}`,
                            value: String(v.id),
                            id: v.id,
                          }));

                        // Phase 9 SC-17: that pool's voucher count, shown
                        // beside winsPerDay so the admin sizes the daily
                        // number against real stock. No new fetch — sums
                        // across the vouchers currently selected/assigned
                        // for this (mall, tier) row, drawn from the row's
                        // already-loaded voucherPool and assignedVouchers
                        // (assignedIds dedupes so a voucher already assigned
                        // to this campaign isn't counted twice from the pool
                        // side too).
                        //
                        // Quick task 260820-gyo: sums `remaining`, not raw
                        // `voucherCount` — summing the raw quantity
                        // overstates the pool the moment any unit is issued,
                        // and is actively misleading next to a voucher
                        // shared with another campaign. Falls back to
                        // voucherCount only when remaining is absent (older
                        // cached rows).
                        const selectedVoucherItems = [
                          ...(row.assignedVouchers || []),
                          ...row.voucherPool.filter(
                            (v) =>
                              !assignedIds.has(v.id) &&
                              row.voucherIds.includes(v.id)
                          ),
                        ];
                        const rowVoucherCount = selectedVoucherItems.reduce(
                          (sum, v) => sum + (v.remaining ?? v.voucherCount ?? 0),
                          0
                        );

                        return (
                          <div key={row.addid} className='targeting-panel-row'>
                            <span className='targeting-panel-tier-name'>
                              {row.tierName}
                            </span>
                            <div className='targeting-panel-minspend'>
                              <NumberInput
                                label={undefined}
                                id={`entryMinSpend-${row.addid}`}
                                name='minSpendThreshold'
                                placeholder='Minimum spend'
                                value={row.minSpendThreshold}
                                onChange={(e) =>
                                  setEntryMinSpend(row.addid, e.target.value)
                                }
                                disabled={isLocked}
                              />
                              {row.isOverridden && (
                                <span
                                  className='targeting-override-dot'
                                  title='Custom value — differs from the bulk amount'
                                />
                              )}
                            </div>
                            <div className='targeting-panel-winsperday'>
                              <NumberInput
                                label={undefined}
                                id={`entryWinsPerDay-${row.addid}`}
                                name='winsPerDay'
                                placeholder='Wins per day'
                                value={row.winsPerDay}
                                onChange={(e) =>
                                  setEntryWinsPerDay(row.addid, e.target.value)
                                }
                                disabled={isLocked}
                              />
                              {selectedVoucherItems.length > 0 && (
                                <span className='campaign-hint-text'>
                                  {rowVoucherCount === 1
                                    ? '1 voucher remaining'
                                    : `${rowVoucherCount} vouchers remaining`}
                                </span>
                              )}
                            </div>
                            <div className='targeting-panel-vouchers'>
                              <MultiSelectDropdown
                                id={`entryVouchers-${row.addid}`}
                                name='voucherIds'
                                options={voucherOptions}
                                // voucherOptions deliberately EXCLUDES the
                                // already-assigned vouchers (they render as
                                // their own cards below). So the dropdown's
                                // value must exclude them too — feeding it
                                // ids it has no option for makes the widget
                                // report a selection that silently omits
                                // them on the next change.
                                value={row.voucherIds
                                  .filter((id) => !assignedIds.has(id))
                                  .map(String)}
                                onChange={(e) => {
                                  const selectedStrIds = e?.target?.value || [];
                                  const picked = selectedStrIds
                                    .map(
                                      (strId) =>
                                        row.voucherPool.find(
                                          (v) => String(v.id) === strId
                                        )?.id
                                    )
                                    .filter((id) => id !== undefined);
                                  // This dropdown only ever owns the
                                  // not-yet-assigned half of the selection.
                                  // Carry the assigned ids through untouched
                                  // — they are removed via each card's own
                                  // control, and dropping them here would
                                  // make submitCampaign diff them as
                                  // released and detach vouchers the admin
                                  // never chose to remove.
                                  const keptAssigned = row.voucherIds.filter(
                                    (id) => assignedIds.has(id)
                                  );
                                  setEntryVouchers(row.addid, [
                                    ...keptAssigned,
                                    ...picked,
                                  ]);
                                }}
                              />
                              {row.voucherPool.length === 0 &&
                                (row.assignedVouchers || []).length === 0 && (
                                  // Quick task 260820-gyo: "unassigned" is a
                                  // retired concept — the pool is now every
                                  // gamification voucher with stock
                                  // remaining, regardless of who holds it.
                                  // An empty pool now means no voucher has
                                  // any stock left for this Mall/Tier, not
                                  // that none are "free".
                                  <p className='campaign-hint-text'>
                                    {pointsConfigured ? (
                                      <>
                                        No vouchers with stock remaining for
                                        this Mall/Tier. This target will
                                        reward points instead.
                                      </>
                                    ) : (
                                      <>
                                        No vouchers with stock remaining for
                                        this Mall/Tier. At least one voucher
                                        is required — flag one as Reserve for
                                        Game in Voucher Management, or remove
                                        this target.
                                      </>
                                    )}
                                  </p>
                                )}

                              {/* Backend field error (D-06/D-08) mapped to
                                  this targeting entry via
                                  fieldErrors[entry.addid]. */}
                              {fieldErrors?.[row.addid] && (
                                <span className='error-text'>
                                  {fieldErrors[row.addid]}
                                </span>
                              )}

                              {/* Backend field errors (D-06/D-08) for a
                                  freshly-selected (not-yet-assigned) voucher
                                  — fieldErrors[`${addid}-voucher-${id}`]. */}
                              {row.voucherIds
                                .filter((voucherId) => !assignedIds.has(voucherId))
                                .map(
                                  (voucherId) =>
                                    fieldErrors?.[
                                      `${row.addid}-voucher-${voucherId}`
                                    ] && (
                                      <span
                                        key={voucherId}
                                        className='error-text'
                                      >
                                        {
                                          fieldErrors[
                                            `${row.addid}-voucher-${voucherId}`
                                          ]
                                        }
                                      </span>
                                    )
                                )}

                              {/* CAMP-07 edit-mode: already-assigned voucher
                                  rows. Each row's remove control binds to
                                  isVoucherRemovable(voucher) — NOT blanket
                                  isLocked — so a DEACTIVATED/pre-start
                                  zero-issuance voucher stays removable
                                  (releases to the pool on submit,
                                  CAMP-07/D-12) while an issued voucher or any
                                  voucher under ACTIVE/ENDED stays locked. */}
                              {(row.assignedVouchers || []).length > 0 && (
                                <div className='targeting-assigned-vouchers'>
                                  {row.assignedVouchers.map((voucher) => {
                                    const issued =
                                      (voucher.voucherCount ?? 0) -
                                      (voucher.remaining ?? 0);
                                    // Quantity editing temporarily hidden — kept
                                    // for re-enable:
                                    // const draftUpdate = voucherUpdates.find(
                                    //   (u) => u.voucherId === voucher.id
                                    // );
                                    // const qtyValue = draftUpdate
                                    //   ? draftUpdate.voucherCount
                                    //   : voucher.voucherCount ?? '';
                                    const voucherLabel =
                                      voucher.voucherTitle ||
                                      `Voucher #${voucher.id}`;
                                    return (
                                      <div
                                        key={voucher.id}
                                        className='assigned-voucher-card'
                                      >
                                        <div className='flex gap-10 campaign-voucher-row assigned-voucher-head'>
                                          <Checkbox
                                            id={`assignedVoucher-${row.addid}-${voucher.id}`}
                                            name={`assignedVoucher-${row.addid}-${voucher.id}`}
                                            checked
                                            disabled={!isVoucherRemovable(voucher)}
                                            onChange={() =>
                                              handleRemoveAssignedVoucher(
                                                entryIdx,
                                                voucher.id
                                              )
                                            }
                                          />
                                          <div className='assigned-voucher-meta'>
                                            <span className='assigned-voucher-title'>
                                              {voucherLabel}
                                            </span>
                                            <span className='assigned-voucher-sub'>
                                              {`${voucher.remaining} remaining`}
                                              {issued > 0
                                                ? ` · ${issued} issued`
                                                : ''}
                                            </span>
                                            {/* Quick task 260820-gyo (D-1/H6): this
                                                remaining count is the voucher's real
                                                GLOBAL remaining — deliberately never
                                                divided between sharing campaigns. This
                                                label is what stops it being read as
                                                this campaign's exclusive stock. */}
                                            {voucher.sharedWith?.length > 0 && (
                                              <span className='assigned-voucher-sub'>
                                                {`Shared with ${voucher.sharedWith
                                                  .map((c) => c.name)
                                                  .filter(Boolean)
                                                  .join(', ')}`}
                                              </span>
                                            )}
                                          </div>
                                          {/* Quantity editor temporarily hidden — re-enable by
                                              uncommenting this block, the qtyValue/draftUpdate
                                              consts above, and the hook props in the destructure.
                                          <div className='assigned-voucher-qty'>
                                            <label htmlFor={`voucherQty-${row.addid}-${voucher.id}`}>
                                              Quantity
                                            </label>
                                            <NumberInput
                                              label={undefined}
                                              id={`voucherQty-${row.addid}-${voucher.id}`}
                                              name='voucherCount'
                                              min={String(issued)}
                                              placeholder='Qty'
                                              value={qtyValue}
                                              onChange={(e) =>
                                                handleVoucherUpdateChange(
                                                  voucher.id,
                                                  e.target.value
                                                )
                                              }
                                            />
                                          </div>
                                          */}
                                        </div>
                                        {/* Quantity-edit error (hidden with the editor):
                                        {voucherUpdateErrors?.[voucher.id] && (
                                          <span className='error-text'>
                                            {voucherUpdateErrors[voucher.id]}
                                          </span>
                                        )}
                                        */}
                                        {fieldErrors?.[
                                          `${row.addid}-voucher-${voucher.id}`
                                        ] && (
                                          <span className='error-text'>
                                            {
                                              fieldErrors[
                                                `${row.addid}-voucher-${voucher.id}`
                                              ]
                                            }
                                          </span>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {error?.targeting && (
            <span className='error-text'>{error.targeting}</span>
          )}

          {/* RWD-04/D-05: inline coverage summary, live at the bottom of
              the section — the submit-time confirm is unchanged. */}
          <p className='targeting-coverage-line'>{coverageLine}</p>
        </div>

        {/* Phase 9 D-07/D-08 issuance-control guidance — non-blocking, never
            disables Save. Mirrors the server's assertIssuanceControlRule
            (09-06-SUMMARY.md); the server's own rejection message on save is
            the authoritative one (D-11: "the dropdown is UX, not the
            security boundary"). */}
        {issuanceRuleUnmet && (
          <p className='error-text campaign-issuance-hint'>{issuanceHint}</p>
        )}

        {/* Bill-window guidance, same contract as the issuance hint above:
            advisory, never disables Save, mirrors the server's
            assertBillDatesRequiredWhenBillReading. Worth surfacing because the
            requirement is NOT implied by the trigger — adding a per-pool min
            spend to a Birthday campaign silently makes it bill-reading, and
            without this the admin only found out on Save. */}
        {billDatesMissing && (
          <p className='error-text campaign-issuance-hint'>
            Bill Start Date and Bill End Date are required
            {values.triggerType === 'SINGLE_DAY_SPEND'
              ? ' for a Single-Day Spend campaign.'
              : ' because a Minimum Spend is set on a targeted mall and tier.'}
          </p>
        )}

        {/* Publish (toggle card) — UAT rework: moved out of Basics, now the
            last field on the form, immediately above Save. When on, the
            campaign is shown to members (within Start/End Date if either is
            set). Restyled to match the Create Voucher toggle-card pattern
            (Round 2 UAT) — reuses the global .toggle-card class from
            src/index.css for the cards themselves.

            The ROW, however, is deliberately NOT the global .toggle-group.
            That class was turned into a CSS grid (auto-fit, minmax(272px,
            1fr)) for Create Voucher's four toggles, and sharing it here broke
            this two-toggle row three ways at once: `display: grid` silently
            overrode the `flex` utility on this div (same specificity, later
            rule in src/index.css); `auto-fit` COLLAPSED the two unused tracks
            so each remaining track became half the form width; and `wd-33`
            then resolved against that half-width track instead of the row,
            shrinking each card to ~197px with a ~450px hole between them.

            Four toggles that should stretch to fill a row and two that should
            not are genuinely different layouts, so this row owns its own
            (.campaign-toggle-row in index.scss) rather than sharing one that
            has to satisfy both. Do NOT re-add .toggle-group here. */}
        <div className='wd-100 flex flex-wrap campaign-toggle-row'>
          <div className='wd-33 toggle-card'>
            <div className='toggle-card_header'>
              <span className='toggle-card_label tooltip-on-hover'>
                Always Win
              </span>
              <span
                className={`toggle-card_badge ${
                  values.alwaysWin ? 'is-on' : 'is-off'
                }`}
              >
                {values.alwaysWin ? 'Active' : 'Inactive'}
              </span>
            </div>
            <Toggle
              checked={values.alwaysWin}
              icons={false}
              disabled={isLocked}
              onChange={() => toggleAlwaysWin()}
            />
          </div>
          <div className='wd-33 toggle-card'>
            <div className='toggle-card_header'>
              <span className='toggle-card_label tooltip-on-hover'>
                Publish
              </span>
              <span
                className={`toggle-card_badge ${
                  publishDisplayValue ? 'is-on' : 'is-off'
                }`}
              >
                {publishDisplayValue ? 'Active' : 'Inactive'}
              </span>
            </div>
            {/* Reads publishDisplayValue, not values.publish: past the end
                date the campaign is not reaching anyone, so showing this ON
                (as it did) told admins the opposite of what the listing's
                Ended badge told them. The switch still moves freely — Save
                is what refuses. The stored value is untouched; see
                publishDisplayValue in useCreateCampaign.js. */}
            <Toggle
              checked={publishDisplayValue}
              icons={false}
              onChange={() => togglePublish()}
            />
          </div>
        </div>

        {/* Why Publish opens off on an ended campaign, and why Save will
            refuse if it is switched back on. Deliberately OUTSIDE the toggle
            card: an inline message in there grows the card and shunts the
            Save button down the page (UAT feedback). Sits with the issuance
            and bill-window hints above, which use this same slot and class. */}
        {isPastEndDate && (
          <p className='error-text campaign-issuance-hint'>
            {publishBlockedByEndDate
              ? 'This campaign has expired, so it cannot be published. Extend the Campaign End Date, or switch Publish off to save your other changes.'
              : 'This campaign has expired and is no longer shown to members. Extend the Campaign End Date before publishing it again.'}
          </p>
        )}

        {/* Save Campaign/Save Changes is wired to the real atomic
            POST /campaigns (create) or PUT /campaigns/update (edit, CAMP-07)
            submit — coverage warning + dual-shape error handling live in
            handleCreateCampaign/useCreateCampaign.js (D-05/D-06/D-07). */}
        <div className='d-flex justify-content-start'>
          <Button
            className='mt1'
            onClick={handleCreateCampaign}
            variant='Save'
            name={campaignId ? 'Save Changes' : 'Save Campaign'}
            isDisabled={pageLoading ? true : false}
          />
          <Button
            className='mt1 cancel-button ms-2'
            onClick={() => navigate(-1)}
            variant='Cancel'
            name='Cancel'
            isDisabled={pageLoading ? true : false}
          />
        </div>
      </div>
      {pageLoading && <PageLoader pageLoading={pageLoading} />}
    </div>
  );
};

export default CreateCampaign;
